import { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { refreshAccessToken, logout } from "@/shared/api/axios";
import { getFieldErrorMessage } from "@/shared/api/helpers";
import { toast } from "react-hot-toast";
import { appSession } from "@/shared/lib/storage";

async function addAuthHeader(config: InternalAxiosRequestConfig<any>) {
  if (config?.url?.includes("/refresh_token")) {
    return config;
  }

  const isAuthEndpoint = config?.url?.includes("/login");
  if (isAuthEndpoint) {
    return config;
  }

  const userData = appSession.getItem();
  if (!userData) {
    logout();
    return Promise.reject(new Error("No authentication data available"));
  }

  const accessToken = userData?.access_token;

  if (
    !accessToken ||
    typeof accessToken !== "string" ||
    accessToken.trim() === ""
  ) {
    try {
      const authHeader = await refreshAccessToken();
      config.headers.Authorization = authHeader;

      return config;
    } catch (error) {
      logout();
      return Promise.reject(
        new Error("Failed to refresh invalid access token")
      );
    }
  }

  config.headers.Authorization = `Bearer ${accessToken.trim()}`;
  return config;
}

function responseErrorHandlerBuilder(instance: AxiosInstance) {
  return async (err: any) => {
    const originalRequest = err.config;

    // Handle refresh token errors
    if (originalRequest?.url?.includes("/refresh_token")) {
      logout();
      return Promise.reject(err);
    }

    // Handle expired token
    if (
      err.response?.data.errors?.[0]?.msg.includes("Signature has expired") ||
      (err.response?.data.python_exc?.includes("Signature has expired") &&
        !originalRequest._retry)
    ) {
      originalRequest._retry = true;
      try {
        const authHeader = await refreshAccessToken();
        instance.defaults.headers.common["Authorization"] = authHeader;

        return instance(originalRequest);
      } catch (error) {
        logout();
        return Promise.reject(error);
      }
    }

    // Handle wrong refresh token
    if (err.response?.data.errors?.[0]?.msg.includes("Wrong refresh token")) {
      logout();
      return Promise.reject(err);
    }

    // Handle 403 errors
    if (err.response?.status === 403) {
      toast.error("Access denied");
      return Promise.reject(err);
    }

    // Handle 401 errors
    if (err.response?.status === 401) {
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const authHeader = await refreshAccessToken();
          instance.defaults.headers.common["Authorization"] = authHeader;

          return instance(originalRequest);
        } catch (err) {
          logout();
          return Promise.reject(err);
        }
      } else {
        logout();
        return Promise.reject(err);
      }
    }

    // Handle other errors with toast notifications
    if (err.response?.data) {
      const error = err;
      if (
        error.response?.data.type === "field" &&
        error.response?.data.service === "gateway"
      ) {
        toast.error(getFieldErrorMessage(error)!);
      } else if (
        error.response?.data.service === "gateway.auth" ||
        error.response?.data.type !== "field"
      ) {
        toast.error(getFieldErrorMessage(error)!);
      } else {
        toast.error(getFieldErrorMessage(error)!);
      }
    } else if (err instanceof Error) {
      toast.error(err.message);
    }

    return Promise.reject(err);
  };
}

export const registerInterceptors = (
  apiInstances: Record<string, AxiosInstance>
) => {
  Object.values(apiInstances).forEach((instance) => {
    instance.interceptors.request.use(addAuthHeader);
    instance.interceptors.response.use(
      (response) => response,
      responseErrorHandlerBuilder(instance)
    );
  });
};
